/**
 * Shared heuristics for TiTiler COG band selection and DEM colourisation.
 *
 * Default to single-band (`bidx=1`) unless the layer is clearly RGB imagery.
 * Requesting `bidx=1&bidx=2&bidx=3` on a 1-band float DEM makes TiTiler
 * return 400 ("band index 2 out of range") and the map looks empty.
 */

export type RasterBandHintInput = {
  readonly id?: string;
  readonly kind?: string;
  readonly label?: string | null;
  readonly title?: string | null;
  readonly family?: string | null;
  readonly artifactKind?: string | null;
  readonly terrainDerivative?: string | null;
};

function blobOf(input: RasterBandHintInput): string {
  return [
    input.id,
    input.label,
    input.title,
    input.family,
    input.artifactKind,
    input.terrainDerivative,
    input.kind,
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();
}

/** True for orthophoto / RGB multi-band imagery that needs bidx=1,2,3. */
export function isLikelyRgbImagery(input: RasterBandHintInput): boolean {
  const family = String(input.family ?? '').toLowerCase();
  const artifact = String(input.artifactKind ?? '').toLowerCase();
  const blob = blobOf(input);
  if (artifact.startsWith('terrain-') || input.terrainDerivative) return false;
  if (family === 'terrain' || family === 'dem' || family === 'elevation' || family === 'landcover') {
    return false;
  }
  if (/(hillshade|slope|aspect|ndsm|contour)/.test(blob)) return false;
  if (/(^|[^a-z])(dem|dtm|dsm|elevation|hypsometr)([^a-z]|$)/.test(blob)) return false;
  return (
    family === 'imagery'
    || blob.includes('ortho')
    || blob.includes('orthophoto')
    || blob.includes('rgb')
    || artifact === 'imagery-basemap'
    || artifact === 'imagery-basemap-pmtiles'
  );
}

/**
 * True when TiTiler should be asked for a single band only.
 * Inverse of {@link isLikelyRgbImagery}, with an explicit terrain/kind fast-path.
 */
export function isLikelySingleBandRaster(input: RasterBandHintInput): boolean {
  if (input.kind === 'terrain') return true;
  return !isLikelyRgbImagery(input);
}

/** True when a hypsometric / gray stretch should be applied for visibility. */
export function isDemLikeRaster(input: RasterBandHintInput): boolean {
  if (input.kind === 'terrain') return true;
  const family = String(input.family ?? '').toLowerCase();
  if (family === 'terrain' || family === 'dem' || family === 'elevation') return true;
  const artifact = String(input.artifactKind ?? '').toLowerCase();
  if (artifact.startsWith('terrain-') || Boolean(input.terrainDerivative)) return true;
  const blob = blobOf(input);
  if (/(hillshade|slope|aspect|ndsm)/.test(blob)) return true;
  if (/(^|[^a-z])(dem|dtm|dsm|elevation|hypsometr|rastercalc)/.test(blob)) return true;
  return false;
}

/** Query suffix for TiTiler tile templates. */
export function titilerBidxQuery(input: RasterBandHintInput): string {
  return isLikelyRgbImagery(input) ? '&bidx=1&bidx=2&bidx=3' : '&bidx=1';
}
